import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import LinearProgress from '@mui/material/LinearProgress';
import Chip from '@mui/material/Chip';
import { styled } from '@mui/material/styles';
import FlipIcon from '@mui/icons-material/Flip';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import ShuffleIcon from '@mui/icons-material/Shuffle';
import { useGameStats } from '../hooks/useGameStats';

const FlashcardContainer = styled(Box)(({ theme }) => ({
  perspective: '1000px',
  width: '100%',
  maxWidth: '600px',
  height: '400px',
  margin: '0 auto',
  marginBottom: theme.spacing(4),
}));

const FlashcardInner = styled(Box)(({ theme, flipped }) => ({
  position: 'relative',
  width: '100%',
  height: '100%',
  textAlign: 'center',
  transition: 'transform 0.6s',
  transformStyle: 'preserve-3d',
  transform: flipped ? 'rotateY(180deg)' : 'rotateY(0deg)',
}));

const FlashcardSide = styled(Card)(({ theme }) => ({
  position: 'absolute',
  width: '100%',
  height: '100%',
  backfaceVisibility: 'hidden',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '24px',
  boxShadow: '0 8px 32px rgba(102, 126, 234, 0.1)',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  '&:hover': {
    boxShadow: '0 12px 48px rgba(102, 126, 234, 0.15)',
  },
}));

const FlashcardFront = styled(FlashcardSide)(({ theme }) => ({
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  color: '#f1f5f9',
}));

const FlashcardBack = styled(FlashcardSide)(({ theme }) => ({
  background: '#1a1a2e',
  transform: 'rotateY(180deg)',
  border: '2px solid rgba(102, 126, 234, 0.3)',
}));

const ControlsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  gap: theme.spacing(2),
  marginTop: theme.spacing(3),
}));

const ProgressContainer = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(4),
}));

const StatsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  gap: theme.spacing(3),
  marginTop: theme.spacing(3),
  flexWrap: 'wrap',
}));

const ActionButton = styled(Button)(({ theme }) => ({
  borderRadius: '12px',
  padding: theme.spacing(1, 3),
  fontWeight: 600,
  textTransform: 'none',
}));

export default function Flashcards({ content, moduleId }) {
  const [cards, setCards] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const [masteredCards, setMasteredCards] = useState(new Set());
  const [difficultCards, setDifficultCards] = useState(new Set());
  const [shuffled, setShuffled] = useState(false);
  const gameStats = useGameStats();

  useEffect(() => {
    // Parse content to create flashcards from headers and their content
    const lines = content.split('\n');
    const flashcards = [];
    let currentCard = null;

    lines.forEach((line, index) => {
      // Check for headers (concepts to study)
      const h3Match = line.match(/^###\s+(.+)$/);
      const h4Match = line.match(/^####\s+(.+)$/);
      
      if (h3Match || h4Match) {
        if (currentCard) {
          flashcards.push(currentCard);
        }
        currentCard = {
          id: `card-${index}`,
          front: (h3Match || h4Match)[1].replace(/\*\*/g, '').trim(),
          back: '',
        };
      } else if (currentCard && line.trim() && !line.startsWith('#')) {
        // Add content as the answer
        if (currentCard.back) {
          currentCard.back += '\n';
        }
        currentCard.back += line.trim();
      }
    });

    if (currentCard && currentCard.back) {
      flashcards.push(currentCard);
    }

    // Also create flashcards from bullet points with strong text
    lines.forEach((line, index) => {
      const bulletMatch = line.match(/^\s*[\*\-]\s+\*\*([^:]+):\*\*\s*(.+)$/);
      if (bulletMatch) {
        flashcards.push({
          id: `bullet-${index}`,
          front: bulletMatch[1].trim(),
          back: bulletMatch[2].trim(),
        });
      }
    });

    setCards(flashcards);
  }, [content]);

  const handleFlip = () => {
    setFlipped(!flipped);
  };

  const handleNext = () => {
    setFlipped(false);
    setTimeout(() => {
      setCurrentIndex((prev) => (prev + 1) % cards.length);
    }, 300);
  };

  const handlePrevious = () => {
    setFlipped(false);
    setTimeout(() => {
      setCurrentIndex((prev) => (prev - 1 + cards.length) % cards.length);
    }, 300);
  };

  const handleMastered = () => {
    const cardId = cards[currentIndex].id;
    const wasAlreadyMastered = masteredCards.has(cardId);
    setMasteredCards(prev => new Set(prev).add(cardId));
    setDifficultCards(prev => {
      const newSet = new Set(prev);
      newSet.delete(cardId);
      return newSet;
    });
    
    // Track mastered flashcard in gamification
    if (!wasAlreadyMastered) {
      gameStats.incrementFlashcardsMastered(1);
    }
    
    handleNext();
  };

  const handleDifficult = () => {
    const cardId = cards[currentIndex].id;
    setDifficultCards(prev => new Set(prev).add(cardId));
    setMasteredCards(prev => {
      const newSet = new Set(prev);
      newSet.delete(cardId);
      return newSet;
    });
    handleNext();
  };

  const handleShuffle = () => {
    const shuffledCards = [...cards].sort(() => Math.random() - 0.5);
    setCards(shuffledCards);
    setCurrentIndex(0);
    setFlipped(false);
    setShuffled(true);
  };

  const handleReset = () => {
    setMasteredCards(new Set());
    setDifficultCards(new Set());
    setCurrentIndex(0);
    setFlipped(false);
  };

  if (cards.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="h6" sx={{ color: '#cbd5e1' }}>
          Nenhum flashcard disponível para este módulo.
        </Typography>
      </Box>
    );
  }

  const currentCard = cards[currentIndex];
  const progress = ((currentIndex + 1) / cards.length) * 100;
  const masteredCount = masteredCards.size;
  const difficultCount = difficultCards.size;

  return (
    <Box>
      {/* Progress */}
      <ProgressContainer>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="body2" sx={{ color: '#cbd5e1' }}>
            Cartão {currentIndex + 1} de {cards.length}
          </Typography>
          <Typography variant="body2" sx={{ color: '#667eea', fontWeight: 600 }}>
            {Math.round(progress)}% Completo
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{
            height: 8,
            borderRadius: 4,
            backgroundColor: 'rgba(102, 126, 234, 0.1)',
            '& .MuiLinearProgress-bar': {
              background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
              borderRadius: 4,
            },
          }}
        />
      </ProgressContainer>

      {/* Flashcard */}
      <FlashcardContainer onClick={handleFlip}>
        <FlashcardInner flipped={flipped}>
          <FlashcardFront elevation={0}>
            <CardContent sx={{ p: 4 }}>
              <Typography variant="h5" component="div" sx={{ fontWeight: 700 }}>
                {currentCard.front}
              </Typography>
              <Typography variant="body2" sx={{ mt: 3, opacity: 0.9, color: '#cbd5e1' }}>
                Clique para ver a resposta
              </Typography>
            </CardContent>
          </FlashcardFront>
          <FlashcardBack elevation={0}>
            <CardContent sx={{ p: 4 }}>
              <Typography variant="body1" sx={{ color: '#e2e8f0', lineHeight: 1.8 }}>
                {currentCard.back}
              </Typography>
            </CardContent>
          </FlashcardBack>
        </FlashcardInner>
      </FlashcardContainer>

      {/* Controls */}
      <ControlsContainer>
        <IconButton 
          onClick={handlePrevious}
          disabled={currentIndex === 0}
          size="large"
        >
          <NavigateBeforeIcon />
        </IconButton>

        <Box sx={{ display: 'flex', gap: 1 }}>
          <ActionButton
            variant="outlined"
            color="error"
            startIcon={<CloseIcon />}
            onClick={handleDifficult}
            disabled={!flipped}
          >
            Difícil
          </ActionButton>
          <ActionButton
            variant="contained"
            color="success"
            startIcon={<CheckIcon />}
            onClick={handleMastered}
            disabled={!flipped}
          >
            Dominado
          </ActionButton>
        </Box>

        <IconButton 
          onClick={handleNext}
          disabled={currentIndex === cards.length - 1}
          size="large"
        >
          <NavigateNextIcon />
        </IconButton>
      </ControlsContainer>

      {/* Stats */}
      <StatsContainer>
        <Chip
          icon={<CheckIcon />}
          label={`${masteredCount} Dominados`}
          color="success"
          variant="outlined"
        />
        <Chip
          icon={<CloseIcon />}
          label={`${difficultCount} Difíceis`}
          color="error"
          variant="outlined"
        />
        <Chip
          label={`${cards.length - masteredCount - difficultCount} Restantes`}
          variant="outlined"
        />
      </StatsContainer>

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 4 }}>
        <Button
          variant="outlined"
          startIcon={<ShuffleIcon />}
          onClick={handleShuffle}
          sx={{ borderRadius: '12px' }}
        >
          Embaralhar
        </Button>
        <Button
          variant="outlined"
          startIcon={<RestartAltIcon />}
          onClick={handleReset}
          sx={{ borderRadius: '12px' }}
        >
          Reiniciar
        </Button>
      </Box>
    </Box>
  );
}